// src/services/tableConfigCache.ts

interface CacheEntry {
	data: Record<string, any>;
	timestamp: number;
	version: number;
}

interface CacheConfig {
	ttl: number; // Time to live in milliseconds
	maxAge: number; // Maximum age before forced refresh
}

/**
 * Singleton cache manager for table configurations
 * Implements intelligent caching with TTL, versioning, and invalidation
 */
class TableConfigCacheManager {
	private static instance: TableConfigCacheManager;
	private cache: CacheEntry | null = null;
	private pendingRequest: Promise<Record<string, any>> | null = null;
	private config: CacheConfig = {
		ttl: 5 * 60 * 1000, // 5 minutes default TTL
		maxAge: 30 * 60 * 1000, // 30 minutes max age
	};

	private constructor() {
		// Subscribe to storage events for cross-tab synchronization
		if (typeof window !== "undefined") {
			window.addEventListener("storage", this.handleStorageChange);
		}
	}

	/**
	 * Get singleton instance
	 */
	public static getInstance(): TableConfigCacheManager {
		if (!TableConfigCacheManager.instance) {
			TableConfigCacheManager.instance = new TableConfigCacheManager();
		}
		return TableConfigCacheManager.instance;
	}

	/**
	 * Configure cache behavior
	 */
	public configure(config: Partial<CacheConfig>): void {
		this.config = { ...this.config, ...config };
	}

	/**
	 * Check if cache is valid
	 */
	private isCacheValid(): boolean {
		if (!this.cache) return false;

		const now = Date.now();
		const age = now - this.cache.timestamp;

		// Check if cache has exceeded TTL
		if (age > this.config.ttl) {
			console.log("📦 Cache expired (TTL exceeded)");
			return false;
		}

		// Check if cache has exceeded max age
		if (age > this.config.maxAge) {
			console.log("📦 Cache expired (max age exceeded)");
			return false;
		}

		return true;
	}

	/**
	 * Get cached data or fetch from API
	 */
	public async getConfigurations(
		fetchFn: () => Promise<Record<string, any>>,
		forceRefresh: boolean = false,
	): Promise<Record<string, any>> {
		// Return cached data if valid and not forcing refresh
		if (!forceRefresh && this.isCacheValid() && this.cache) {
			console.log("✅ Returning cached table configurations");
			return this.cache.data;
		}

		// If there's already a pending request, wait for it
		if (this.pendingRequest) {
			console.log("⏳ Waiting for pending configuration request");
			return this.pendingRequest;
		}

		// Fetch fresh data
		console.log("🔄 Fetching fresh table configurations");
		this.pendingRequest = this.fetchAndCache(fetchFn);

		try {
			const result = await this.pendingRequest;
			return result;
		} finally {
			this.pendingRequest = null;
		}
	}

	/**
	 * Fetch data and update cache
	 */
	private async fetchAndCache(
		fetchFn: () => Promise<Record<string, any>>,
	): Promise<Record<string, any>> {
		try {
			const data = await fetchFn();

			// Update cache
			this.cache = {
				data,
				timestamp: Date.now(),
				version: (this.cache?.version || 0) + 1,
			};

			// Persist to localStorage for cross-tab sync
			this.persistToLocalStorage();

			console.log("✅ Table configurations cached successfully");
			return data;
		} catch (error) {
			console.error("❌ Error fetching table configurations:", error);

			// Return stale cache if available (graceful degradation)
			if (this.cache) {
				console.warn("⚠️ Returning stale cache due to fetch error");
				return this.cache.data;
			}

			throw error;
		}
	}

	/**
	 * Update cache with new data (after save)
	 */
	public updateCache(data: Record<string, any>): void {
		this.cache = {
			data,
			timestamp: Date.now(),
			version: (this.cache?.version || 0) + 1,
		};

		this.persistToLocalStorage();
		console.log("✅ Cache updated with new data");
	}

	/**
	 * Update a specific table's configuration
	 */
	public updateTableConfig(tableId: string, config: any): void {
		if (!this.cache) {
			console.warn("⚠️ No cache to update");
			return;
		}

		this.cache.data[tableId] = config;
		this.cache.timestamp = Date.now();
		this.cache.version += 1;

		this.persistToLocalStorage();
		console.log(`✅ Updated cache for table: ${tableId}`);
	}

	/**
	 * Invalidate cache (force refresh on next access)
	 */
	public invalidate(): void {
		this.cache = null;
		this.clearLocalStorage();
		console.log("🗑️ Cache invalidated");
	}

	/**
	 * Clear specific table from cache
	 */
	public clearTable(tableId: string): void {
		if (this.cache && this.cache.data[tableId]) {
			delete this.cache.data[tableId];
			this.cache.version += 1;
			this.persistToLocalStorage();
			console.log(`🗑️ Cleared cache for table: ${tableId}`);
		}
	}

	/**
	 * Persist cache to localStorage for cross-tab sync
	 */
	private persistToLocalStorage(): void {
		if (typeof window === "undefined") return;

		try {
			const cacheData = {
				...this.cache,
				// Add expiry timestamp
				expiresAt: Date.now() + this.config.ttl,
			};
			localStorage.setItem("tableConfigCache", JSON.stringify(cacheData));
		} catch (error) {
			console.warn("⚠️ Failed to persist cache to localStorage:", error);
		}
	}

	/**
	 * Load cache from localStorage on init
	 */
	public loadFromLocalStorage(): void {
		if (typeof window === "undefined") return;

		try {
			const stored = localStorage.getItem("tableConfigCache");
			if (!stored) return;

			const parsed = JSON.parse(stored);

			// Check if cache has expired
			if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
				console.log("📦 LocalStorage cache expired");
				this.clearLocalStorage();
				return;
			}

			this.cache = {
				data: parsed.data,
				timestamp: parsed.timestamp,
				version: parsed.version,
			};

			console.log("✅ Cache loaded from localStorage");
		} catch (error) {
			console.warn("⚠️ Failed to load cache from localStorage:", error);
			this.clearLocalStorage();
		}
	}

	/**
	 * Clear localStorage cache
	 */
	private clearLocalStorage(): void {
		if (typeof window === "undefined") return;
		localStorage.removeItem("tableConfigCache");
	}

	/**
	 * Handle storage events for cross-tab synchronization
	 */
	private handleStorageChange = (event: StorageEvent): void => {
		if (event.key !== "tableConfigCache") return;

		if (!event.newValue) {
			// Cache was cleared in another tab
			this.cache = null;
			console.log("🔄 Cache cleared in another tab");
			return;
		}

		try {
			const parsed = JSON.parse(event.newValue);

			// Only update if version is newer
			if (!this.cache || parsed.version > this.cache.version) {
				this.cache = {
					data: parsed.data,
					timestamp: parsed.timestamp,
					version: parsed.version,
				};
				console.log("🔄 Cache updated from another tab");
			}
		} catch (error) {
			console.warn("⚠️ Failed to parse storage event:", error);
		}
	};

	/**
	 * Get cache statistics
	 */
	public getStats(): {
		isCached: boolean;
		age: number | null;
		version: number | null;
		tableCount: number;
	} {
		if (!this.cache) {
			return {
				isCached: false,
				age: null,
				version: null,
				tableCount: 0,
			};
		}

		return {
			isCached: true,
			age: Date.now() - this.cache.timestamp,
			version: this.cache.version,
			tableCount: Object.keys(this.cache.data).length,
		};
	}

	/**
	 * Cleanup on destroy
	 */
	public destroy(): void {
		if (typeof window !== "undefined") {
			window.removeEventListener("storage", this.handleStorageChange);
		}
	}
}

// Export singleton instance
export const tableConfigCache = TableConfigCacheManager.getInstance();

// Initialize cache from localStorage on module load
if (typeof window !== "undefined") {
	tableConfigCache.loadFromLocalStorage();
}
